import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Card,
  Badge,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
} from "react-bootstrap";
import axios from "axios";
import "../../styles/TicketList.css"; // Optional styling file
import { Baseurl } from "../../baseurl";

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Fetch tickets from API
  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Baseurl}/api/contact/all-tickets`
      );
      setTickets(response.data.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch tickets. Please try again.");
      console.error("Error fetching tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  // View ticket details
  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  const handleTicket = async (ticket) => {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const url = `${Baseurl}/api/contact/closeTicket/${ticket._id}`;
    const resp = await axios.put(url, ticket, config);
    if (resp.data.success) {
      setTickets(resp.data?.allTickets);
      handleCloseModal();
    }
  };
  // Filter tickets based on search term and status
  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.emailAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.message?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterStatus === "all") return matchesSearch;
    return matchesSearch;
  });

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading tickets...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <h2 className="mb-4">Contact Form Submissions</h2>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3"
            onClick={fetchTickets}
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <Card.Title>Total Submissions</Card.Title>
              <Card.Text className="display-6">{tickets.length}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <Card.Title>Today</Card.Title>
              <Card.Text className="display-6">
                {
                  tickets.filter((t) => {
                    const today = new Date();
                    const ticketDate = new Date(t.createdAt);
                    return ticketDate.toDateString() === today.toDateString();
                  }).length
                }
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <Card.Title>This Week</Card.Title>
              <Card.Text className="display-6">
                {
                  tickets.filter((t) => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(t.createdAt) > weekAgo;
                  }).length
                }
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stat-card">
            <Card.Body>
              <Card.Title>Unique Emails</Card.Title>
              <Card.Text className="display-6">
                {new Set(tickets.map((t) => t.emailAddress)).size}
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter Section */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group controlId="searchTickets">
                <Form.Label>Search Tickets</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, subject, or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="filterStatus">
                <Form.Label>Filter by</Form.Label>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Tickets</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant="outline-primary"
                onClick={() => fetchTickets()}
                className="w-100"
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tickets Table */}
      <Card>
        <Card.Body>
          <div className="table-responsive">
            <Table hover striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Subject</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      No tickets found
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket, index) => (
                    <tr key={ticket._id}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{ticket.name}</strong>
                      </td>
                      <td>
                        <a
                          href={`mailto:${ticket.emailAddress}`}
                          className="text-decoration-none"
                        >
                          {ticket.emailAddress}
                        </a>
                      </td>
                      <td>
                        <a
                          href={`tel:${ticket.phone}`}
                          className="text-decoration-none"
                        >
                          {ticket.phone}
                        </a>
                      </td>
                      <td>
                        <span
                          className="subject-truncate"
                          title={ticket.subject}
                        >
                          {ticket.subject.length > 50
                            ? `${ticket.subject.substring(0, 50)}...`
                            : ticket.subject}
                        </span>
                      </td>
                      <td>
                        <Badge bg="light" text="dark">
                          {formatDate(ticket.createdAt)}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleViewTicket(ticket)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Pagination Info */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Showing {filteredTickets.length} of {tickets.length} tickets
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Ticket Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Ticket Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTicket && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header>Contact Information</Card.Header>
                    <Card.Body>
                      <p>
                        <strong>Name:</strong> {selectedTicket.name}
                      </p>
                      <p>
                        <strong>Email:</strong> {selectedTicket.emailAddress}
                      </p>
                      <p>
                        <strong>Phone:</strong> {selectedTicket.phone}
                      </p>
                      <p>
                        <strong>Submitted:</strong>{" "}
                        {formatDate(selectedTicket.createdAt)}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="mb-3">
                    <Card.Header>Subject</Card.Header>
                    <Card.Body>
                      <h5>{selectedTicket.subject}</h5>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card>
                <Card.Header>Message</Card.Header>
                <Card.Body>
                  <div className="message-content">
                    {selectedTicket.message}
                  </div>
                </Card.Body>
              </Card>

              <div className="mt-3 text-muted small">
                {/* <p>ID: {selectedTicket._id}</p> */}
                {selectedTicket.updatedAt &&
                  selectedTicket.updatedAt !== selectedTicket.createdAt && (
                    <p>Last Updated: {formatDate(selectedTicket.updatedAt)}</p>
                  )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => handleTicket(selectedTicket)}
          >
            Close Ticket
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TicketList;
